import { styled } from '@linaria/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PullRequestAgentEdit } from '../../../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkDarkest,
  inkLight,
  primaryBase,
  secondaryBase,
  secondaryDarkest,
  secondaryLightest,
  skyLight,
  skyLighter,
  skyLightest,
} from '../../../../../styles/design-tokens';
import { fmtNum, turnDisplayNumber } from '../../../shared/formatters';
import { NotTraced, sessionFallbackTitle, SubtleText, ThisBadge } from '../pr-detail.helpers';
import { sortEdits } from './agent-edits-view.helpers';

type AgentEditsViewProps = {
  edits: PullRequestAgentEdit[];
  currentSessionId: string;
  patchAvailable: boolean;
  /**
   * File-level deduped agent-line totals from the canonical `PullRequestFile`
   * (the same numbers behind the sticky-header kept%). Used for the heading so
   * it never contradicts the header: summing per-edit `authored` double-counts a
   * line rewritten across edits, and the backend already dedupes at the file.
   * These are subtree-scoped (the viewed session), so the heading labels them
   * as "this session" — the rows below can include other sessions' edits.
   */
  authored: number;
  survived: number;
  /**
   * The file's `line_precise` flag. When false the totals are not evidence-based
   * (the backend leaves `authored` raw but zeroes `survived`), so the heading
   * shows a not-traced note instead of a misleading "N unique, 0 landed".
   */
  linePrecise: boolean;
};

type EditRowProps = {
  edit: PullRequestAgentEdit;
  isCurrent: boolean;
  patchAvailable: boolean;
};

/**
 * One agent edit for a file: its turn and session, how many of its unique lines
 * landed in the merge, and an expandable list of the lines that did not.
 * @param {EditRowProps} props - The edit, whether it belongs to the viewed session, and whether a diff exists.
 * @returns {JSX.Element} The edit row.
 */
function EditRow({ edit, isCurrent, patchAvailable }: EditRowProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const notMerged = edit.lines.filter((line) => line.matched_new_line === null);
  const survivedPct = edit.authored > 0 ? Math.min(100, (edit.survived / edit.authored) * 100) : 0;

  return (
    <EditCard>
      <EditHead>
        <EditTurn>
          {t('agent_miner.pull_requests.code.edit_turn', { turn: turnDisplayNumber(edit.turn_number ?? 0) })}
        </EditTurn>
        <Dot>·</Dot>
        {isCurrent ? (
          <ThisBadge>{t('agent_miner.pull_requests.code.edit_this_session')}</ThisBadge>
        ) : (
          <SessionLink
            href={`/application/agent-miner/sessions/${edit.session_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {edit.session_title ?? sessionFallbackTitle(edit.session_id)}
          </SessionLink>
        )}
        {patchAvailable && (
          <EditStat>
            {t('agent_miner.pull_requests.code.edit_stats', {
              landed: fmtNum(edit.survived),
              unique: fmtNum(edit.authored),
            })}
          </EditStat>
        )}
      </EditHead>
      {patchAvailable && (
        <MiniTrack aria-hidden="true">
          <Segment $survivedPct={survivedPct} />
        </MiniTrack>
      )}
      {patchAvailable &&
        (notMerged.length > 0 ? (
          <>
            <Toggle type="button" aria-expanded={open} onClick={() => setOpen((prev) => !prev)}>
              <Chevron $open={open} aria-hidden="true">
                ▶
              </Chevron>
              {t('agent_miner.pull_requests.code.edit_not_merged_toggle', { count: notMerged.length })}
            </Toggle>
            {open && (
              // Mask source code from Sentry Session Replay: these are verbatim
              // not-merged lines. data-sentry-mask masks all descendants.
              <NotMergedList data-sentry-mask>
                {notMerged.map((line) => (
                  <NotMergedLine key={`${line.hunk_index}-${line.line_index}`}>{line.content}</NotMergedLine>
                ))}
              </NotMergedList>
            )}
          </>
        ) : (
          <SubtleText>{t('agent_miner.pull_requests.code.edit_all_landed')}</SubtleText>
        ))}
    </EditCard>
  );
}

/**
 * Agent-side view of one PR file: every edit that touched it, how many of its
 * unique lines landed in the merge, and the exact lines that did not. Lines
 * that did not land are described as such, never as "reworked", because the
 * matcher cannot tell a human rewrite from the agent's own later edit.
 *
 * @param {AgentEditsViewProps} props - The file's edits, the viewed session id, whether a diff exists, the file-level authored/survived totals, and the line-precise flag
 * @returns {JSX.Element} The edits list
 */
export function AgentEditsView({
  edits,
  currentSessionId,
  patchAvailable,
  authored,
  survived,
  linePrecise,
}: AgentEditsViewProps) {
  const { t } = useTranslation();

  if (edits.length === 0) {
    return <NotTraced>{t('agent_miner.pull_requests.code.edits_empty')}</NotTraced>;
  }

  const sorted = sortEdits(edits, currentSessionId);

  return (
    <Wrap>
      {!patchAvailable ? (
        <NotTraced>{t('agent_miner.pull_requests.code.edits_unmatched_hint')}</NotTraced>
      ) : linePrecise ? (
        <Heading>
          {t('agent_miner.pull_requests.code.edits_heading', {
            count: sorted.length,
            unique: fmtNum(authored),
            landed: fmtNum(survived),
          })}
        </Heading>
      ) : (
        <NotTraced>{t('agent_miner.pull_requests.code.edits_not_traced')}</NotTraced>
      )}
      {sorted.map((edit) => (
        <EditRow
          key={edit.file_edit_id}
          edit={edit}
          isCurrent={edit.session_id === currentSessionId}
          patchAvailable={patchAvailable}
        />
      ))}
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Heading = styled.span`
  font: 500 11.5px/1 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const EditCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${skyLight};
  background: ${skyLighter};
`;

const EditHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

const EditTurn = styled.span`
  padding: 2px 7px;
  border-radius: 99px;
  background: ${secondaryLightest};
  color: ${secondaryDarkest};
  font: 600 10px/1.3 'Figtree', sans-serif;
`;

const Dot = styled.span`
  color: ${inkLight};
`;

const SessionLink = styled.a`
  font: 600 12px/1.3 'Figtree', sans-serif;
  color: ${inkDarkest};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;

  &:hover {
    color: ${secondaryBase};
  }
`;

const EditStat = styled.span`
  margin-left: auto;
  font: 500 11px/1 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const MiniTrack = styled.div`
  display: flex;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: ${skyLight};
`;

const Segment = styled.div<{ $survivedPct: number }>`
  height: 100%;
  width: ${(props) => props.$survivedPct}%;
  background: ${primaryBase};
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  color: ${secondaryBase};
  font: 600 11.5px/1 'Figtree', sans-serif;
  cursor: pointer;
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  font-size: 9px;
  transform: ${(props) => (props.$open ? 'rotate(90deg)' : 'none')};
  transition: transform 0.12s ease;
`;

const NotMergedList = styled.pre`
  margin: 0;
  padding: 8px 10px;
  background: ${skyLightest};
  border-radius: 4px;
  border-left: 2px solid ${secondaryBase};
  font: 400 11.5px/1.5 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkDarkest};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow: auto;
`;

const NotMergedLine = styled.div`
  white-space: pre;
`;
